import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { ApiClient } from "src/app/definitions/api/api-client";

import { ApiAccessService } from "../../services/api/api-access.service";
import { ApiAccessActions } from "../actions/api-access.actions";
import { ApiClientState } from "../models/api-client-state";
import { FailState } from "../models/fail-state";
import { AppState } from "../reducers";
import { ApiAccessSelectors } from "../selectors/api-access.selectors";

@Injectable()
export class ApiAccessEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(ApiAccessActions.apiClient.get),
			withLatestFrom(this.store.select(ApiAccessSelectors.apiClients)),
			switchMap(([action, state]) => {
				const client = this.getStoreValue(action, state);
				if (client) {
					return of(ApiAccessActions.apiClient.getSuccess(client));
				} else {
					return of(ApiAccessActions.apiClient.getApi({ clientId: action.clientId }));
				}
			})
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(ApiAccessActions.apiClient.getApi),
			switchMap((action) =>
				this.apiAccessService.getClient(action.clientId).pipe(
					map((client) => ApiAccessActions.apiClient.getSuccess(client)),
					catchError((error) =>
						of(ApiAccessActions.apiClient.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	createClient = createEffect(() =>
		this.actions.pipe(
			ofType(ApiAccessActions.apiClient.createClient),
			switchMap((action) =>
				this.apiAccessService.createClient(action.allowedAuthScopes).pipe(
					map((client) => ApiAccessActions.apiClient.createClientSuccess(client)),
					catchError((error) =>
						of(ApiAccessActions.apiClient.createClientFail(new FailState(error)))
					)
				)
			)
		)
	);

	removeClient = createEffect(() =>
		this.actions.pipe(
			ofType(ApiAccessActions.apiClient.removeClient),
			switchMap((action) =>
				this.apiAccessService.removeClient(action.clientId).pipe(
					map(() =>
						ApiAccessActions.apiClient.removeClientSuccess({
							clientId: action.clientId
						})
					),
					catchError((error) =>
						of(ApiAccessActions.apiClient.removeClientFail(new FailState(error)))
					)
				)
			)
		)
	);

	private getStoreValue(action, state: ApiClientState): ApiClient {
		return _.find(_.get(state, "clients", []), (client) => client.clientId === action.clientId);
	}

	constructor(
		private actions: Actions,
		private apiAccessService: ApiAccessService,
		private store: Store<AppState>
	) {}
}
